import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AlertCircle, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import { GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import { GLOBAL_IMAGES } from "../../assets/asset";
import useResponsiveGoogleWidth from "../../hooks/useResponsiveGoogleWidth";
import {
  extractStudentJwtToken,
  extractStudentUserRecord,
  studentAuthApi,
} from "../../features/auth/authApi";
import { finalizeStudentLogin } from "../../features/auth/useLogin";

const GENERIC_GOOGLE_ERROR = "Unable to sign up with Google. Please try again.";

const CreateAccount = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [googleError, setGoogleError] = useState("");
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const { containerRef: googleButtonRef, buttonWidth: googleButtonWidth } =
    useResponsiveGoogleWidth();

  const handleLoginClick = () => {
    navigate("/login");
  };

  const handleSignupWithEmail = () => {
    navigate("/createaccountform");
  };

  const handleGoogleSignupSuccess = async (credentialResponse) => {
    setGoogleError("");
    setIsGoogleLoading(true);

    try {
      const idToken = credentialResponse.credential;
      if (!idToken) {
        setGoogleError(GENERIC_GOOGLE_ERROR);
        setIsGoogleLoading(false);
        return;
      }

      // Decode the Google ID token to extract the user's display name and email
      let username = "";
      let email = "";
      try {
        const decoded = jwtDecode(idToken);
        email = decoded.email || "";
        username = decoded.name || email.split("@")[0] || "";
      } catch (_decodeError) {
        username = "";
        email = "";
      }

      const response = await studentAuthApi.googleSignup({
        id_token: idToken,
        username,
        email,
      });

      const profileData = extractStudentUserRecord(response, {
        jwtoken: extractStudentJwtToken(response),
      });

      if (!profileData.jwtoken) {
        setGoogleError(GENERIC_GOOGLE_ERROR);
        setIsGoogleLoading(false);
        return;
      }

      finalizeStudentLogin({
        dispatch,
        navigate,
        profileData,
        shouldShowFirstSignupDemo: true,
      });
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || GENERIC_GOOGLE_ERROR;
      setGoogleError(errorMessage);
      toast.error(errorMessage, {
        ariaLabel: "Google Signup Error",
        autoClose: 2500,
        theme: "colored",
        hideProgressBar: true,
        className: "font-mali",
      });
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleGoogleSignupError = () => {
    setGoogleError(GENERIC_GOOGLE_ERROR);
  };

  const buttonClass =
    "flex items-center justify-center rounded-lg py-2 transition duration-300";

  return (
    <div
      className="create-account-page min-h-screen flex items-center justify-center bg-blue-50 font-mali"
      style={{
        backgroundImage: `url(${GLOBAL_IMAGES.onboarding_bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      role="main"
      aria-labelledby="create-account-title"
    >
      <div
        className="create-account-back absolute top-5 left-5 cursor-pointer"
        onClick={() => navigate(-1)}
        role="button"
        aria-label="Go back to the previous page"
      >
        <img
          src={GLOBAL_IMAGES.LoginBackButton}
          alt="Back"
          className="create-account-back-img w-10 h-10"
        />
      </div>
      <div
        className="create-account-card bg-white bg-opacity-60 p-12 rounded-lg shadow-lg max-w-lg w-full"
        role="region"
        aria-labelledby="create-account-title"
        aria-describedby="create-account-description"
      >
        <h2
          id="create-account-title"
          className="create-account-title text-3xl font-bold text-center text-gray-800 mb-6"
        >
          Create account
        </h2>
        <p
          id="create-account-description"
          className="create-account-description text-center text-gray-600 mb-8"
        >
          Login or signup here to use the
          <span className="font-bold text-indigo-600"> MATHDINGO</span>{" "}
          application
        </p>

        <div className="create-account-actions flex flex-col space-y-4 w-full max-w-[400px] mx-auto">
          {/* Google Sign Up */}
          <div className="create-account-google auth-google-shell flex flex-col items-center">
            {isGoogleLoading ? (
              <button
                disabled
                className={`${buttonClass} bg-white text-gray-700 border border-gray-300 w-full opacity-60 cursor-not-allowed`}
              >
                <Loader2 className="animate-spin mr-2" size={20} />
                Signing up...
              </button>
            ) : (
              <div
                ref={googleButtonRef}
                className="auth-google-button-wrap flex min-h-[44px] w-full justify-center"
              >
                <GoogleLogin
                  onSuccess={handleGoogleSignupSuccess}
                  onError={handleGoogleSignupError}
                  text="signup_with"
                  shape="rectangular"
                  size="large"
                  width={googleButtonWidth}
                  logo_alignment="left"
                />
              </div>
            )}
          </div>

          {googleError && (
            <div className="create-account-google-error flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-300 rounded-lg text-red-700 text-sm">
              <AlertCircle size={16} />
              <span>{googleError}</span>
            </div>
          )}

          {/* Divider */}
          <div className="create-account-or-divider flex items-center gap-3">
            <div className="flex-1 border-t border-gray-400"></div>
            <span className="text-gray-500 text-sm font-semibold">or</span>
            <div className="flex-1 border-t border-gray-400"></div>
          </div>

          {/* <button
            className={`${buttonClass} bg-blue-800 text-white hover:bg-blue-900`}
            onClick={handleSignupWithEmail}
            aria-label="Sign up with Email"
          >
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/4/4e/Mail_%28iOS%29.svg"
              alt="Mail"
              className="w-6 h-6 mr-2"
            />
            Signup with Email
          </button> */}

          <button
            className={`${buttonClass} bg-white text-blue-800 border border-blue-300 hover:bg-blue-50`}
            onClick={() => navigate("/createaccount-otp")}
            aria-label="Continue signup with OTP"
          >
            Continue with OTP
          </button>
        </div>

        <div className="text-center text-black mt-6">
          <div className="create-account-divider border-t border-black my-4"></div>
          <p className="create-account-helper text-black-600 md:font-bold">Already registered?</p>
          <button
            className={`${buttonClass} bg-white text-gray-700 border border-gray-300 hover:bg-gray-100`}
            style={{ width: "100%" }}
            onClick={handleLoginClick}
            aria-label="Login to your account"
          >
            Login
          </button>

          <div className="create-account-links flex justify-between mt-4">
            <a
              href="https://mathdingo.com/terms_of_use.html"
              target="_blank"
              rel="noreferrer"
              className="text-black font-bold text-sm underline"
              style={{ flex: 1, textAlign: "left", paddingLeft: "0.5rem" }}
              aria-label="Read subscription use"
            >
              Read Subscription Use
            </a>
            <a
              href="/contact"
              target="_blank"
              rel="noreferrer"
              className="text-black font-bold text-sm underline"
              style={{ flex: 1, textAlign: "right", paddingRight: "0.5rem" }}
              aria-label="Contact us for support"
            >
              Contact Us
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateAccount;
